import { useState, useCallback, useMemo } from "react";

import { TouchpointAttributes } from "hooks/api/use-touchpoints";
import { useCreateFolder } from "hooks/api/use-folders";
import CreateFolderModal from "containers/admin/clients/campaign/campaign-creative-page/components/create-folder/create-folder-modal/create-folder-modal";
import toast from "components/partials/toast/toast";
import { getErrorMessage } from "utilities";

export const useCreateFolderModal = (onSuccess?: () => void) => {
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);

  const createFolderMutation = useCreateFolder();

  const openCreateFolder = useCallback(() => setIsCreateFolderOpen(true), []);
  const closeCreateFolder = useCallback(() => setIsCreateFolderOpen(false), []);

  const handleCreateFolder = useCallback(
    async (data: { name: string; addedTouchpoints?: TouchpointAttributes[] }) => {
      try {
        await createFolderMutation.mutateAsync({
          name: data.name,
          touchpointIds: data.addedTouchpoints?.map((tp) => tp.id) ?? [],
        });
        toast.success({
          data: {
            title: `Created '${data.name}'`,
          },
        });
        onSuccess?.();
        closeCreateFolder();
      } catch (error) {
        toast.error({
          data: {
            title: "Failed to create folder",
            message: getErrorMessage(error),
          },
        });
      }
    },
    [createFolderMutation, closeCreateFolder, onSuccess],
  );

  const createFolderModalMarkup = useMemo(() => {
    if (!isCreateFolderOpen) return null;

    return (
      <CreateFolderModal
        onCloseCallback={closeCreateFolder}
        onConfirmCallback={handleCreateFolder}
        isSubmitting={createFolderMutation.isPending}
      />
    );
  }, [isCreateFolderOpen, closeCreateFolder, handleCreateFolder, createFolderMutation.isPending]);

  return {
    openCreateFolder,
    closeCreateFolder,
    createFolderModalMarkup,
  };
};
